import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { deleteProject } from '../utils.js';
import { saveFile, showSaveDialog } from "../fileUtils.js";

// Sichere localStorage Wrapper
const safeLocalStorageSet = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error("Fehler beim Speichern:", error);
    alert("Speichern nicht möglich. Überprüfen Sie den Speicherplatz.");
  }
};

const safeLocalStorageGet = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error("Fehler beim Lesen:", error);
    return defaultValue;
  }
};

const Startseite = ({ currentProject, setCurrentProject, projects, setProjects }) => {  
  const navigate = useNavigate();
  const [newProjectName, setNewProjectName] = useState("");
  const [importError, setImportError] = useState(null);

  // Initialer Projektlade-Effekt
  useEffect(() => {
    const savedProjects = safeLocalStorageGet("projects", []);
    if (savedProjects.length > 0 && projects.length === 0) {
      setProjects(savedProjects);
    }
  }, [setProjects]);

  // Projekt-Erstellung mit umfangreicher Validierung
  const createNewProject = useCallback(() => {
    const trimmedName = newProjectName.trim();
    
    if (!trimmedName) {
      alert("Bitte geben Sie einen Projektnamen ein.");
      return;
    }

    // Duplikat-Prüfung
    const isDuplicate = projects.some(p => 
      p.name.toLowerCase() === trimmedName.toLowerCase()
    );
    
    if (isDuplicate) {
      alert("Ein Projekt mit diesem Namen existiert bereits.");
      return;
    }

    const newProject = {
      id: Date.now(),
      name: trimmedName,
      createdAt: new Date().toISOString()
    };

    setProjects((prevProjects) => {
      const updatedProjects = [...prevProjects, newProject];
      safeLocalStorageSet("projects", updatedProjects);
      return updatedProjects;
    });

    if (typeof setCurrentProject === "function") {  
      setCurrentProject(newProject);
      safeLocalStorageSet("currentProject", newProject);
      
      // Navigationsverzögerung für besseres UX-Feeling
      setTimeout(() => navigate("/probedaten"), 300);
    }

    setNewProjectName("");
  }, [newProjectName, projects, setProjects, setCurrentProject, navigate]);

  // Projekt auswählen
  const selectProject = useCallback((project) => {
    setCurrentProject(project);
    safeLocalStorageSet("currentProject", project);
    navigate("/probedaten");
  }, [setCurrentProject, navigate]);

  // Projekt löschen mit Bestätigung
  const handleDeleteProject = useCallback((projectId) => {
    if (window.confirm('Projekt wirklich löschen?')) {
      deleteProject(projectId);
      
      const updatedProjects = projects.filter(p => p.id !== projectId);
      setProjects(updatedProjects);
      safeLocalStorageSet("projects", updatedProjects);

      if (currentProject?.id === projectId) {
        setCurrentProject(null);
        localStorage.removeItem("currentProject");
      }
    }
  }, [projects, currentProject, setProjects, setCurrentProject]);

  // Projekt exportieren
const exportProject = useCallback(async (projectId) => {
  const projectData = {
    projectInfo: projects.find(p => p.id === projectId),
    probedaten: safeLocalStorageGet(`probedaten_${projectId}`, []),
    absences: safeLocalStorageGet(`absences_${projectId}`, []),
    scenes: safeLocalStorageGet(`scenes_${projectId}`, []),
    assignments: safeLocalStorageGet(`assignments_${projectId}`, []),
    teamAvailability: safeLocalStorageGet(`teamAvailability_${projectId}`, []),
    artisticTeam: safeLocalStorageGet(`artisticTeam_${projectId}`, []),
    actorEnsemble: safeLocalStorageGet(`actorEnsemble_${projectId}`, []),
    ausgewaehlteSzenen: safeLocalStorageGet(`ausgewaehlteSzenen_${projectId}`, []),
    characters: safeLocalStorageGet(`characters_${projectId}`, [])
  };

  // Vorbereitung der Datei für den Export
  const content = JSON.stringify(projectData, null, 2);
  const defaultFileName = `${projectData.projectInfo.name.replace(/\s+/g, '_')}_backup.json`;
  
  // Browser-Datei-Dialog-Ansatz
  const blob = new Blob([content], { type: 'application/json' });
  
  // Erstelle einen Download-Link, aber lege den Namen explizit fest
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = defaultFileName; // Der vorgeschlagene Dateiname
  
  // Datei direkt speichern (wird im Standard-Download-Ordner gespeichert)
  link.click();
  
  // Bereinigen
  URL.revokeObjectURL(url);
  
}, [projects]);
// Direkt nach dieser Zeile: }, [projects]);
useEffect(() => {
  console.log("ElectronAPI verfügbar?", window.electronAPI ? "Ja" : "Nein");
  if (window.electronAPI) {
    console.log("ElectronAPI Methoden:", Object.keys(window.electronAPI));
  }
}, []);
  // Projekt importieren
  const importProject = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const projectData = JSON.parse(text);

      const newProject = {
        ...projectData.projectInfo,
        id: Date.now()
      };

      // Speichern aller Projektdaten
      const projectStorageKeys = [
        'probedaten', 'absences', 'scenes', 'assignments', 
        'teamAvailability', 'artisticTeam', 'actorEnsemble', 'ausgewaehlteSzenen', 
        'characters' // Figuren zum Import hinzufügen
      ];

      projectStorageKeys.forEach(key => {
        safeLocalStorageSet(`${key}_${newProject.id}`, projectData[key] || []);
      });

      setProjects(prev => {
        const updatedProjects = [...prev, newProject];
        safeLocalStorageSet("projects", updatedProjects);
        return updatedProjects;
      });

      alert('Projekt erfolgreich importiert!');
      setImportError(null);
    } catch (error) {
      console.error('Fehler beim Import:', error);
      setImportError('Fehler beim Importieren. Bitte überprüfen Sie die Datei.');
    }
    
    event.target.value = '';
  }, [setProjects]);

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">Probeplaner - Startseite</h2>
      
      <div className="mb-4 flex items-center space-x-2">
        <input 
          type="text"
          value={newProjectName}
          onChange={(e) => setNewProjectName(e.target.value)}
          placeholder="Neues Projekt Name"
          className="border p-2 flex-grow"
          onKeyPress={(e) => e.key === 'Enter' && createNewProject()}
        />
        <button 
          onClick={createNewProject}  
          className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
        >
          Neues Projekt
        </button>
        
        <input
          type="file"
          accept=".json"
          onChange={importProject}
          className="hidden"
          id="projectImport"
        />
        <label 
          htmlFor="projectImport" 
          className="bg-green-500 text-white p-2 rounded cursor-pointer hover:bg-green-600"
        >
          Importieren
        </label>
      </div>

      {importError && (
        <div className="text-red-500 mb-4">{importError}</div>
      )}

      <div>
        <h3 className="text-xl font-bold mb-2">Gespeicherte Projekte:</h3>
        <ul>
          {projects.length > 0 ? (
            projects.map((project) => (
              <li 
                key={project.id} 
                className="border p-2 mb-1 flex justify-between items-center"
              >
                <span 
                  className={`cursor-pointer flex-grow p-2 ${
                    currentProject?.id === project.id 
                      ? 'bg-blue-300' 
                      : 'hover:bg-gray-200'
                  }`}
                  onClick={() => selectProject(project)}
                >
                  {project.name}
                </span>
                <div className="flex space-x-2">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      exportProject(project.id);
                    }}
                    className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
                  >
                    Exportieren
                  </button>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteProject(project.id);
                    }}
                    className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                  >
                    Löschen
                  </button>
                </div>
              </li>
            ))
          ) : (
            <p className="text-gray-500">Keine Projekte gespeichert.</p>
          )}
        </ul>
      </div>

      <div className="mt-4">
        <h3 className="text-xl font-bold">Aktuelles Projekt:</h3>
        <p>{currentProject ? currentProject.name : "Kein Projekt ausgewählt"}</p>
      </div>
    </div>
  );
};

export default React.memo(Startseite);